import React from "react";
import { useTranslation } from "react-i18next";
import { HailingStepData, VehicleClass, selectedVehicleClasses } from "../mock/data";
import { getPickupTime } from "../tracker/tripStatus.helper";
import {
  HailingQuoteOption,
  HailingQuoteResponse,
} from "../../../api/meHailingQuote.service";

export interface PricesProps {
  state: HailingStepData;
  setState: React.Dispatch<React.SetStateAction<HailingStepData>>;
  onStepComplete: () => void;
  quote?: HailingQuoteResponse | undefined;
  onEdit: () => void;
}

export interface PricedVehicle extends VehicleClass {
  quoteOptionKey: string;
  amount: number;
  isDiscounted: boolean;
  total: number;
  discountedTotal: number;
  fareKind: "meter" | "flat";
}

const quoteAmount = (option: HailingQuoteOption): number | undefined => {
  const matrix = option.priceMatrix;
  const amount = matrix?.discountedTotal ?? matrix?.total ?? matrix?.fixedPrice;
  return typeof amount === "number" && Number.isFinite(amount) && amount >= 0
    ? amount
    : undefined;
};

const isFixedFare = (option: HailingQuoteOption): boolean => {
  const fareType = option.priceMatrix?.fareType?.toUpperCase();
  return fareType
    ? fareType.includes("FIXED")
    : option.priceMatrix?.fixedPrice != null;
};

const toPricedVehicle = (
  option: HailingQuoteOption,
  quoteId: string,
  money: Intl.NumberFormat,
): PricedVehicle | undefined => {
  const amount = quoteAmount(option);
  if (amount === undefined) return undefined;

  const fixedFare = isFixedFare(option);
  const total = option.priceMatrix.total ?? amount;
  const discountedTotal = option.priceMatrix.discountedTotal ?? amount;
  const areas = option.operatingArea ?? [];
  const key = `${option.vehicleClass}:${fixedFare ? "flat" : "meter"}`;
  const iconUrl =
    option.vehicleIconUrl ??
    option.metadata?.vehicleIconUrl;

  return {
    key,
    quoteOptionKey: key,
    quoteId,
    vehicleClassCode: option.vehicleClass,
    operatingArea: areas,
    ...(option.priceMatrix.fareType !== undefined
      ? { fareType: option.priceMatrix.fareType }
      : {}),
    isFixedFare: fixedFare,
    fareKind: fixedFare ? "flat" : "meter",
    discountedTotal,
    total,
    name: option.vehicleClassName,
    blurb: option.vehicleClassDescription ?? "",
    seats: option.metadata?.seatsCount ?? 0,
    bags: option.metadata?.luggageCount ?? 0,
    eta:
      typeof option.eta === "number" && Number.isFinite(option.eta)
        ? option.eta
        : 0,
    meter: amount,
    flat: amount,
    price: money.format(amount),
    ...(iconUrl !== undefined ? { iconUrl } : {}),
    amount,
    isDiscounted:
      typeof option.priceMatrix.total === "number" &&
      typeof option.priceMatrix.discountedTotal === "number" &&
      option.priceMatrix.discountedTotal < option.priceMatrix.total,
  };
};

export const toPricedVehicles = (
  options: HailingQuoteOption[],
  quoteId: string,
  money: Intl.NumberFormat,
): PricedVehicle[] => {
  const grouped = new Map<string, PricedVehicle>();
  for (const option of options) {
    const vehicle = toPricedVehicle(option, quoteId, money);
    if (!vehicle) continue;

    const existing = grouped.get(vehicle.quoteOptionKey);
    if (!existing) {
      grouped.set(vehicle.quoteOptionKey, vehicle);
      continue;
    }

    const areas = new Set([
      ...(existing.operatingArea ?? []),
      ...(vehicle.operatingArea ?? []),
    ]);
    const preferred = vehicle.discountedTotal > existing.discountedTotal
      ? vehicle
      : existing;
    const allAreaIconEntry = options.find(
      (entry) =>
        entry.vehicleClass === vehicle.vehicleClassCode &&
        (entry.allAreaVehicleIconUrl ??
          entry.metadata?.allAvailableOperatingAreaVehicleIconUrl),
    );
    const allAreaIcon =
      allAreaIconEntry?.allAreaVehicleIconUrl ??
      allAreaIconEntry?.metadata?.allAvailableOperatingAreaVehicleIconUrl;

    grouped.set(vehicle.quoteOptionKey, {
      ...preferred,
      eta: preferred.eta || existing.eta || vehicle.eta,
      operatingArea: [...areas],
      ...(areas.size > 1 && allAreaIcon ? { iconUrl: allAreaIcon } : {}),
    });
  }
  return [...grouped.values()];
};

/** The selected class with the highest fare: the "up to" amount. */
export const highestFare = (vehicles: PricedVehicle[]): PricedVehicle | undefined =>
  vehicles.reduce<PricedVehicle | undefined>(
    (highest, vehicle) => (!highest || vehicle.amount > highest.amount ? vehicle : highest),
    undefined,
  );

export const fareFormatter = (language: string): Intl.NumberFormat =>
  new Intl.NumberFormat(language.startsWith("zh") ? "zh-HK" : "en-HK", {
    style: "currency",
    currency: "HKD",
    maximumFractionDigits: 2,
  });

const Prices: React.FC<PricesProps> = ({
  state,
  setState,
  onStepComplete,
  onEdit,
  quote,
}) => {
  const { t, i18n } = useTranslation("hailing");
  const money = React.useMemo(
    () => fareFormatter(i18n.language),
    [i18n.language],
  );
  const vehicles = React.useMemo(
    () => toPricedVehicles(quote?.quotes ?? [], quote?.quoteId ?? "", money),
    [money, quote],
  );
  const currentFareOptions = vehicles.filter(
    (vehicle) => vehicle.fareKind === state.fareKind,
  );
  // The rider can pick several classes within one fare kind; the first option until they pick.
  const selectedKeys = selectedVehicleClasses(state).map((vehicle) => vehicle.quoteOptionKey);
  const pickedVehicles = currentFareOptions.filter((vehicle) =>
    selectedKeys.includes(vehicle.quoteOptionKey),
  );
  const firstOption = currentFareOptions[0];
  const selectedVehicles =
    pickedVehicles.length > 0 ? pickedVehicles : firstOption ? [firstOption] : [];
  const selectedVehicle = highestFare(selectedVehicles);
  const isMultiple = selectedVehicles.length > 1;
  const selectionSignature = selectedVehicles.map((vehicle) => vehicle.quoteOptionKey).join("|");
  const stateSignature = selectedKeys.join("|");

  const withSelection = React.useCallback(
    (previous: HailingStepData, selection: PricedVehicle[]): HailingStepData => {
      const primary = highestFare(selection);
      if (!primary) return previous;
      return {
        ...previous,
        vehicleClass: primary,
        vehicleClasses: selection,
        fareKind: primary.fareKind,
        fareEstimate:
          selection.length > 1
            ? t("steps.prices.upTo", { amount: money.format(primary.amount) })
            : money.format(primary.amount),
      };
    },
    [money, t],
  );

  React.useEffect(() => {
    if (!selectedVehicle) return;
    if (
      stateSignature === selectionSignature &&
      state.vehicleClass.quoteOptionKey === selectedVehicle.quoteOptionKey &&
      state.vehicleClass.quoteId === quote?.quoteId &&
      state.fareKind === selectedVehicle.fareKind
    ) {
      return;
    }
    setState((previous) => {
      if (previous.fareKind !== state.fareKind) return previous;
      // Work from the latest state, not this render's: a tap can land before this update runs,
      // and this render's selection would undo it.
      const keys = selectedVehicleClasses(previous).map((vehicle) => vehicle.quoteOptionKey);
      const stillOffered = currentFareOptions.filter((vehicle) =>
        keys.includes(vehicle.quoteOptionKey),
      );
      const selection = stillOffered.length > 0 ? stillOffered : selectedVehicles;
      const primary = highestFare(selection);
      if (
        primary &&
        keys.length === selection.length &&
        previous.vehicleClass.quoteOptionKey === primary.quoteOptionKey &&
        previous.vehicleClass.quoteId === primary.quoteId
      ) {
        return previous;
      }
      return withSelection(previous, selection);
    });
    // selectionSignature stands in for selectedVehicles, which is rebuilt every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    quote?.quoteId,
    selectionSignature,
    stateSignature,
    selectedVehicle,
    setState,
    state.fareKind,
    state.vehicleClass.quoteId,
    state.vehicleClass.quoteOptionKey,
    withSelection,
  ]);

  // A tap adds or removes a class; the last selected one stays.
  const handleVehicleSelect = (vehicle: PricedVehicle) => {
    const isSelected = selectedVehicles.some(
      (selected) => selected.quoteOptionKey === vehicle.quoteOptionKey,
    );
    if (isSelected && selectedVehicles.length === 1) return;
    const selection = isSelected
      ? selectedVehicles.filter((selected) => selected.quoteOptionKey !== vehicle.quoteOptionKey)
      : [...selectedVehicles, vehicle];
    setState((previous) => withSelection(previous, selection));
  };

  const setFareKind = (kind: "meter" | "flat") => {
    const options = vehicles.filter((vehicle) => vehicle.fareKind === kind);
    const vehicle =
      options.find(
        (option) =>
          option.vehicleClassCode === state.vehicleClass.vehicleClassCode,
      ) ?? options[0];
    if (vehicle) {
      setState((previous) => withSelection(previous, [vehicle]));
    } else {
      setState((previous) => ({ ...previous, fareKind: kind, vehicleClasses: [] }));
    }
  };

  const selectedFareKind = state.fareKind;
  const fareText = selectedVehicle
    ? `${selectedFareKind === "meter" ? `${t("steps.prices.est")} ` : ""}${money.format(selectedVehicle.amount)}`
    : "";
  const ctaPrice = isMultiple ? t("steps.prices.upTo", { amount: fareText }) : fareText;

  const handleContinue = () => {
    if (!selectedVehicle) return;
    setState((previous) => withSelection(previous, selectedVehicles));
    onStepComplete();
  };

  return (
    <div className="flex flex-1 flex-col">
      <button
        type="button"
        onClick={onEdit}
        className="flex w-full items-center gap-3.5 rounded-2xl bg-white px-4 py-3 text-left transition-shadow hover:shadow-[0_4px_16px_rgba(15,21,26,0.08)]"
      >
        {/* Each marker sits in a box as tall as its place line (h-5), and the
            connector fills the 9px gap between the lines, so the markers stay
            centred on the pickup and drop-off text. */}
        <span className="flex flex-col items-center self-start">
          <span className="flex h-5 items-center">
            <span className="box-border h-[11px] w-[11px] rounded-full border-[3px] border-ink-100" />
          </span>
          <span
            className="h-[9px] w-0.5"
            style={{
              background:
                "repeating-linear-gradient(#B5B8BA 0 3px, transparent 3px 6px)",
            }}
          />
          <span className="flex h-5 items-center">
            <span className="h-[11px] w-[11px] rounded-[3px] bg-ink-100" />
          </span>
        </span>

        <span className="flex min-w-0 flex-1 flex-col gap-[9px]">
          <span className="truncate text-[15px] font-medium leading-5 text-ink-100">
            {state.pickupPlace}
          </span>
          <span className="truncate text-[15px] font-medium leading-5 text-ink-100">
            {state.dropoffPlace}
          </span>
          {(quote?.distanceMeters != null || quote?.durationSeconds != null) && (
            <span className="text-[11px] leading-4 text-[#6B7075]">
              {[
                quote?.distanceMeters != null && quote.distanceMeters > 0
                  ? t("steps.where.route.distance", {
                      distance: (quote.distanceMeters / 1000).toFixed(1),
                    })
                  : null,
                quote?.durationSeconds != null && quote.durationSeconds > 0
                  ? t("steps.where.route.duration", {
                      duration: Math.round(quote.durationSeconds / 60),
                    })
                  : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>
          )}
        </span>

        <span className="flex flex-col items-end gap-[6px]">
          <span className="text-[13px] font-medium text-brand-blue">
            {t("steps.prices.edit")}
          </span>
          <span className="whitespace-nowrap text-[12px] text-[#6B7075]">
            {state.scheduledTime
              ? getPickupTime(t, state.scheduledTime)
              : t("steps.prices.now")}
          </span>
        </span>
      </button>

      <h1 className="mt-[22px] text-[21px] font-bold tracking-[-0.4px] text-ink-100 sm:text-[22px] md:text-[24px]">
        {t("steps.prices.title")}
      </h1>

      <div
        role="tablist"
        aria-label={t("steps.prices.fareType")}
        className="mt-3 flex gap-1 rounded-[14px] bg-[#E3E4E5] p-1"
      >
        <button
          type="button"
          role="tab"
          aria-selected={state.fareKind === "meter"}
          onClick={() => setFareKind("meter")}
          className={`flex h-[42px] flex-1 items-center justify-center gap-1.5 rounded-[11px] text-[15px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue ${
            state.fareKind === "meter"
              ? "bg-white text-ink-100 shadow-[0_1px_4px_rgba(15,21,26,0.14)]"
              : "text-[#6B7075]"
          }`}
        >
          <span className="font-material-symbols text-[18px] leading-none">
            speed
          </span>
          {t("steps.prices.meter")}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={state.fareKind === "flat"}
          onClick={() => setFareKind("flat")}
          className={`flex h-[42px] flex-1 items-center justify-center gap-1.5 rounded-[11px] text-[15px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue ${
            state.fareKind === "flat"
              ? "bg-white text-ink-100 shadow-[0_1px_4px_rgba(15,21,26,0.14)]"
              : "text-[#6B7075]"
          }`}
        >
          <span className="font-material-symbols text-[18px] leading-none">
            lock
          </span>
          {t("steps.prices.flat")}
        </button>
      </div>

      <div className="mt-2 flex items-start gap-1.5 text-[13px] leading-[1.45] text-[#555B60]">
        <span className="font-material-symbols mt-0.5 text-[16px] leading-none">
          info
        </span>
        <span>
          {state.fareKind === "meter"
            ? t("steps.prices.meterNote")
            : t("steps.prices.flatNote")}
        </span>
      </div>
      {currentFareOptions.length > 1 && (
        <p className="mt-1 text-[13px] leading-[1.45] text-[#555B60]">
          {t("steps.prices.multiSelectHint")}
        </p>
      )}

      {currentFareOptions.length > 0 ? (
        <div className="mt-3 flex flex-col gap-2">
          {currentFareOptions.map((vehicle) => {
            const selected = selectedVehicles.some(
              (picked) => picked.quoteOptionKey === vehicle.quoteOptionKey,
            );
            const areas = vehicle.operatingArea ?? [];
            return (
              <button
                key={vehicle.quoteOptionKey}
                type="button"
                onClick={() => handleVehicleSelect(vehicle)}
                aria-pressed={selected}
                className={`flex min-h-[76px] w-full items-center gap-3 rounded-[18px] border-2 bg-white py-3 pl-3 pr-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2 sm:gap-3.5 sm:pl-3.5 sm:pr-4 ${
                  selected ? "border-brand-blue" : "border-transparent"
                }`}
              >
                <span className="flex h-[50px] w-[56px] flex-none items-center justify-center sm:w-[64px]">
                  {vehicle.iconUrl ? (
                    <img
                      src={vehicle.iconUrl}
                      alt={`${vehicle.name} vehicle`}
                      loading="lazy"
                      className="max-h-[48px] max-w-full object-contain"
                    />
                  ) : (
                    <span className="h-10 w-full rounded-md bg-[#F2F3F4]" aria-hidden="true" />
                  )}
                </span>

                <span className="flex min-w-0 flex-1 flex-col gap-1">
                  <span className="break-words text-[15px] font-semibold leading-5 text-ink-100 sm:text-[16px]">
                    {vehicle.name}
                  </span>
                  <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] leading-[1.45] text-[#555B60] sm:text-[14px]">
                    {vehicle.seats > 0 && (
                      <span
                        role="img"
                        aria-label={`${vehicle.seats} ${t("steps.prices.seats")}`}
                        className="inline-flex items-center gap-1"
                      >
                        <span aria-hidden="true" className="font-material-symbols text-[17px] leading-none">
                          person
                        </span>
                        <span>{vehicle.seats}</span>
                      </span>
                    )}
                    {vehicle.bags > 0 && (
                      <span
                        role="img"
                        aria-label={`${vehicle.bags} ${t("steps.prices.luggage")}`}
                        className="inline-flex items-center gap-1"
                      >
                        <span aria-hidden="true" className="font-material-symbols text-[17px] leading-none">
                          luggage
                        </span>
                        <span>{vehicle.bags}</span>
                      </span>
                    )}
                    {vehicle.eta > 0 && (
                      <span>{t("steps.prices.minAway", { minutes: vehicle.eta })}</span>
                    )}
                  </span>
                  {areas.length > 0 && (
                    <span className="break-words text-[12px] leading-[1.4] text-[#6B7075]">
                      {t("steps.prices.operatingArea", { areas: areas.join(", ") })}
                    </span>
                  )}
                  {vehicle.blurb && (
                    <span className="whitespace-normal break-words text-[13px] leading-[1.5] text-[#555B60] sm:text-[14px]">
                      {vehicle.blurb}
                    </span>
                  )}
                </span>

                <span className="flex flex-none flex-col items-end gap-0.5">
                  <span className="flex items-baseline gap-1">
                    {vehicle.fareKind === "meter" && (
                      <span className="text-[12px] text-[#6B7075]">
                        {t("steps.prices.est")}
                      </span>
                    )}
                    <span className="text-[19px] font-bold text-ink-100">
                      {money.format(vehicle.amount)}
                    </span>
                  </span>
                  {vehicle.isDiscounted && (
                    <span className="text-[11px] text-[#6B7075] line-through">
                      {money.format(vehicle.total)}
                    </span>
                  )}
                  {vehicle.fareKind === "flat" && (
                    <span className="flex items-center gap-0.5 text-[11px] font-medium text-brand-blue">
                      <span className="font-material-symbols text-[13px] leading-none">
                        lock
                      </span>
                      {t("steps.prices.fixed")}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      ) : (
        <div
          role="status"
          className="mt-4 rounded-2xl bg-white px-4 py-5 text-[14px] leading-[1.5] text-[#555B60]"
        >
          <p>{t("steps.prices.noFareOptions", { fare: t(`steps.prices.${state.fareKind}`) })}</p>
          <button
            type="button"
            onClick={onEdit}
            className="mt-3 min-h-11 font-semibold text-brand-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue"
          >
            {t("steps.prices.editRoute")}
          </button>
        </div>
      )}

      {selectedVehicle && (
        <div className="mt-auto flex flex-col gap-2 pt-5">
          <button
            type="button"
            onClick={handleContinue}
            className="flex min-h-14 items-center justify-between gap-3 rounded-2xl bg-brand-blue px-4 text-[15px] font-semibold text-white transition-colors hover:bg-[#0F66B4] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 sm:px-5 sm:text-[16px] md:text-[17px]"
          >
            <span className="min-w-0 text-left leading-tight">
              {isMultiple
                ? t("steps.prices.continueMultiple", { count: selectedVehicles.length })
                : t("steps.prices.continue", { name: selectedVehicle.name })}
            </span>
            <span className="flex-none whitespace-nowrap">{ctaPrice}</span>
          </button>
          {selectedFareKind === "meter" && (
            <p className="text-[11px] leading-[1.45] text-[#6B7075]">
              {t("steps.prices.meterDisclaimer")}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default Prices;
